import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { ErrorBlock, Pill, useLoadable } from '../components/ui'
import { FolderTreePane } from '../components/FolderTree'
import { folderIdFromLink } from './moveFolderLink'
import { buildExplorer, type ExplorerNode } from './myAccessTree'
import type { MoveCheck, MoveResult, MyAccess } from '../types'

/* Move folder for me (29 Oct 2026). Google lets only a drive Manager move a folder between shared drives,
   and a Manager sees every limited-access folder in the drive. So the person never gets Manager: they pick a
   folder they could already move and an RMS folder they can edit, and the portal's own account moves it.
   Every check re-runs on the server, as them, when they press the button. */

interface Destination { folderId: string; label: string; driveName: string }

const short = (driveName: string) => driveName.replace(/^\d+\s+RMS\s+/, '')

/** RMS folders this person can add to, labelled with their path (drive › … › folder). */
function destinations(access: MyAccess): Destination[] {
  const out: Destination[] = []
  for (const d of access.drives) {
    for (const f of d.folders) {
      if (f.state !== 'visible' || f.access !== 'edit') continue
      const path = (f.path ?? []).map((p) => p.name)
      out.push({ folderId: f.folderId, driveName: d.driveName, label: [short(d.driveName), ...path, f.name].join(' › ') })
    }
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
}

/* The destination as a tree, like My access (Tony, 30 Oct): open a folder to see what is inside, down to
   level 4 (the nightly folderTree). Only folders they can add to are picked; deeper ones by link. */
const canAdd = (n: ExplorerNode) => n.folder?.state === 'visible' && n.folder.access === 'edit'

const plural = (n: number, one: string) => `${n.toLocaleString()} ${one}${n === 1 ? '' : 's'}`

export default function MoveFolderRoute() {
  const [params, setParams] = useSearchParams()
  const [access, retryAccess] = useLoadable<MyAccess>(() => api.getAccess(), () => false, [])
  const options = useMemo(() => (access.status === 'ready' ? destinations(access.data) : []), [access])
  const roots = useMemo(() => (access.status === 'ready' ? buildExplorer(access.data.drives) : []), [access])

  const [link, setLink] = useState('')
  const [filter, setFilter] = useState('')
  const destId = params.get('to') ?? ''
  const [check, setCheck] = useState<MoveCheck | null>(null)
  // A pasted link can name any subfolder, not just the listed RMS folders; the server checks it as them.
  const [result, setResult] = useState<MoveResult | null>(null)
  const linked = [result?.destination, check?.destination].find((d) => d?.folderId === destId) ?? null
  const dest: Destination | null = options.find((o) => o.folderId === destId)
    ?? (destId ? {
      folderId: destId,
      driveName: linked?.driveName ?? '',
      label: linked?.name ? [linked.driveName ? short(linked.driveName) : null, linked.name].filter(Boolean).join(' › ') : 'The folder from your link',
    } : null)
  const [checking, setChecking] = useState(false)
  const [moving, setMoving] = useState(false)
  const [error, setError] = useState<{ message: string; correlationId?: string } | null>(null)

  const runCheck = async (to: string) => {
    if (!link.trim()) return
    setChecking(true); setError(null); setResult(null)
    try {
      setCheck(await api.checkMove(link.trim(), to || undefined))
    } catch (e) {
      const err = e as Error & { correlationId?: string }
      setCheck(null); setError({ message: err.message, correlationId: err.correlationId })
    } finally {
      setChecking(false)
    }
  }
  const pickDest = (id: string) => {
    const next = new URLSearchParams(params)
    next.set('to', id)
    setParams(next, { replace: true })
    if (check?.source) void runCheck(id)
  }
  const clearDest = () => {
    const next = new URLSearchParams(params)
    next.delete('to')
    setParams(next, { replace: true })
    if (check?.source) void runCheck('')
  }
  const move = async () => {
    if (!dest) return
    setMoving(true); setError(null)
    try {
      setResult(await api.moveFolder(link.trim(), dest.folderId))
      setCheck(null)
    } catch (e) {
      const err = e as Error & { correlationId?: string }
      setError({ message: err.message, correlationId: err.correlationId })
    } finally {
      setMoving(false)
    }
  }
  const startOver = () => { setLink(''); setCheck(null); setResult(null); setError(null) }

  if (access.status === 'error') return <ErrorBlock message={access.message} correlationId={access.correlationId} onRetry={retryAccess} />

  const sourceOk = !!check?.source && check.blockers.length === 0
  const ready = sourceOk && !!dest && check?.ok

  if (result) {
    return (
      <>
        <p className="eyebrow">Move a folder</p>
        <h1 style={{ marginBottom: 'var(--s-sm)' }}>Moved</h1>
        <div className="block" style={{ borderLeft: '3px solid var(--rule)' }}>
          <h3 style={{ marginTop: 0 }}>{result.source.name}</h3>
          <p>
            Now in <b>{dest?.label ?? result.destination.name}</b>, with {plural(result.files, 'file')} and {plural(result.folders, 'folder')} inside.
            It counts towards your department on the leaderboard from tomorrow morning.
          </p>
          <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap' }}>
            <a className="btn btn--secondary" href={result.webViewLink} target="_blank" rel="noreferrer">Open in Drive</a>
            <button className="btn btn--secondary" onClick={startOver}>Move another folder</button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <p className="eyebrow">Move a folder</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>Move a folder into RMS</h1>
      <p className="note" style={{ marginBottom: 'var(--s-lg)' }}>
        Google only lets drive managers move a whole folder from one shared drive to another. Pick the folder and
        where it goes, and the portal moves it for you, with everything inside. You need to be able to move the
        folder where it is now, open everything in it, and add to the destination.
      </p>

      <section className="mv-step">
        <h2 className="wizard__sub">1. Which folder?</h2>
        <label htmlFor="mv-link">Link to the folder</label>
        <p className="note" style={{ margin: '0 0 var(--s-xs)' }}>In Drive, right-click the folder, then Share › Copy link.</p>
        <div className="mv-row">
          <input id="mv-link" className="field" value={link} placeholder="https://drive.google.com/drive/folders/…"
            onChange={(e) => { setLink(e.target.value); setCheck(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') void runCheck(destId) }} />
          <button className="btn btn--secondary" disabled={!link.trim() || checking} onClick={() => runCheck(destId)}>
            {checking ? 'Checking…' : 'Check folder'}
          </button>
        </div>
        {check?.source && (
          <p style={{ marginTop: 'var(--s-sm)' }}>
            <b>{check.source.name}</b>
            {check.source.driveName ? ` in ${short(check.source.driveName)}` : ''}
            {check.files + check.folders > 0 && <span className="note"> · {plural(check.files, 'file')}, {plural(check.folders, 'folder')} inside</span>}
          </p>
        )}
        {check && check.blockers.length > 0 && (
          <div className="block block--narrow" role="alert" style={{ marginTop: 'var(--s-sm)' }}>
            <Pill tone="amber">Cannot move this one here</Pill>
            {check.blockers.map((b) => <p key={b.code} style={{ marginTop: 'var(--s-sm)' }}>{b.message}</p>)}
          </div>
        )}
      </section>

      <section className="mv-step">
        <h2 className="wizard__sub">2. Where does it go?</h2>
        {dest ? (
          <p>
            <b>{dest.label}</b>{' '}
            <button className="btn btn--ghost" onClick={clearDest}>Change</button>
          </p>
        ) : (
          <>
            <label htmlFor="mv-filter">Pick a folder where the folder goes into</label>
            <input id="mv-filter" className="field" value={filter} placeholder="Type to narrow, or paste a folder link"
              onChange={(e) => {
                const id = folderIdFromLink(e.target.value)
                if (id) { setFilter(''); pickDest(id) } else setFilter(e.target.value)
              }} />
            <p className="note" style={{ margin: 'var(--s-xs) 0 0' }}>
              <span className="ma-dot" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} aria-hidden="true" />
              You can move a folder here. Click a folder to see the folders inside. Deeper than shown? Paste its link.
            </p>
            {access.status === 'loading' ? (
              <div className="skeleton" style={{ height: '8rem', marginTop: 'var(--s-sm)' }} />
            ) : options.length === 0 ? (
              <p className="note" style={{ marginTop: 'var(--s-sm)' }}>
                You cannot add to any RMS folder yet. Ask the folder owner for edit access from <Link to="/owners">Owners</Link>.
              </p>
            ) : (
              <div className="mv-tree">
                <FolderTreePane roots={roots} selectedId={destId} onSelect={pickDest} query={filter}
                  canSelect={canAdd} label="Destination folders" pickLabel="Move here" />
              </div>
            )}
          </>
        )}
      </section>

      <section className="mv-step">
        <h2 className="wizard__sub">3. Move it</h2>
        {ready && check?.source && dest ? (
          <>
            <p>
              Moves <b>{check.source.name}</b> and everything in it ({plural(check.files, 'file')}, {plural(check.folders, 'folder')}) to <b>{dest.label}</b>.
            </p>
            <p className="note">
              After the move, the folder has the same access as its new place. People who could see it only through its
              old location will lose access. Links to it keep working.
            </p>
            <button className="btn btn--primary" disabled={moving} onClick={move}>
              {moving ? 'Moving…' : 'Move folder for me'}
            </button>
          </>
        ) : (
          <p className="note">{checking ? 'Checking…' : 'Check a folder and pick where it goes first.'}</p>
        )}
        {error && (
          <div className="block block--narrow" role="alert" style={{ marginTop: 'var(--s-sm)' }}>
            <Pill tone="red">Not moved</Pill>
            <p style={{ marginTop: 'var(--s-sm)' }}>{error.message}</p>
            {error.correlationId && <code className="state__sub">{error.correlationId}</code>}
          </div>
        )}
      </section>
    </>
  )
}
